// SETTINGS V2 — one team member, in BOTH shapes (SETTINGS_V2_SP6 §5.3 / §5.7):
// desktop/tablet = a TEAM_GRID grid row (cell-by-cell parity with the legacy
// EmployeeRow); phone <768 = a card with a tap-to-expand detail. Renders the
// access chip, dates (via teamV2Utils — no Moment), the localized remaining line,
// status colouring, and hosts the kebab (TeamRowV2Menu). userName is shown (never
// email — parity); visitPurpose renders under the chip for ANY accessType-truthy
// user (parity). Inline styles + scoped CSS-var tokens; no MUI/sx/dt/Tailwind.
// SEC-REQ-2: no import-time side effects.
import React, { useState } from "react";
import type { TFunction } from "i18next";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { User, AccessTypeCode, StatusTypeCode } from "../../models/userModels";
import { getStatusLabel } from "../../utils/employeeUtils";
import { useRoleLabel } from "../../utils/useRoleLabel";
import { getTimeRemainingV2, formatDate, formatDateTime } from "./teamV2Utils";
import { TEAM_GRID } from "./TeamHeadV2";
import TeamRowV2Menu, { TeamRowV2MenuProps } from "./TeamRowV2Menu";

const ellipsis: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// Access chip — permanent = accent pill on accent-soft; temporary = amber outline.
const AccessPill: React.FC<{
  accessType: AccessTypeCode;
  compact?: boolean;
  t: TFunction;
}> = ({ accessType, compact, t }) => {
  const permanent = accessType === AccessTypeCode.permanent;
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: compact ? 9 : 9.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        padding: "2px 7px",
        borderRadius: 999,
        whiteSpace: "nowrap",
        flexShrink: 0,
        color: permanent ? "var(--mod-accent)" : "var(--amber)",
        background: permanent ? "var(--mod-accent-soft)" : "transparent",
        border: `1px solid ${
          permanent
            ? "var(--mod-accent-line)"
            : "color-mix(in oklab, var(--amber) 55%, var(--border))"
        }`,
      }}
    >
      {t(permanent ? "settings:employee" : "settings:temporary") as string}
    </span>
  );
};

export interface TeamRowV2Props
  extends Omit<TeamRowV2MenuProps, "user"> {
  user: User;
  index: number;
}

const TeamRowV2: React.FC<TeamRowV2Props> = ({ user, index, ...handlers }) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();
  const getRoleLabel = useRoleLabel();
  const [expanded, setExpanded] = useState(false);
  const statusLabel = getStatusLabel(user.status, (k) => t(k) as string);

  const remaining = getTimeRemainingV2(user.expirationDate, t);
  const expired = remaining?.expired || false;
  const disabled = user.status === StatusTypeCode.disabled;
  const roleLabel = user.role?.name ? getRoleLabel(user.role) : "";
  const expirationText = user.expirationDate
    ? formatDateTime(user.expirationDate)
    : "—";

  const menu = <TeamRowV2Menu user={user} {...handlers} />;

  // ─── Phone card (§5.7) ───
  if (isPhone) {
    const toggle = () => setExpanded((v) => !v);
    return (
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            toggle();
          }
        }}
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 10,
          boxShadow: "var(--shadow)",
          padding: "11px 13px",
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          cursor: "pointer",
          animation: `sv2-card-reveal 0.4s ease-out ${index * 0.06}s both`,
        }}
      >
        {/* Header: name + access chip + kebab */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: "var(--ink)",
              flex: 1,
              minWidth: 0,
              ...ellipsis,
            }}
          >
            {user.firstName} {user.lastName}
          </span>
          {user.accessType && (
            <AccessPill accessType={user.accessType} compact t={t} />
          )}
          {menu}
        </div>

        {/* Mail line */}
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--muted)",
            marginBottom: 6,
            ...ellipsis,
          }}
        >
          {user.userName}
        </div>

        {/* Meta: role → until (+remaining) → status */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 11.5, color: "var(--faint)", minWidth: 0, ...ellipsis }}>
            {roleLabel}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, flexShrink: 0 }}>
            <span style={{ fontWeight: expired ? 600 : 400, color: expired ? "var(--red)" : "var(--muted)" }}>
              {expirationText}
            </span>
            {remaining && !expired && remaining.text && (
              <span style={{ color: "var(--muted)" }}>{`  ${remaining.text}`}</span>
            )}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: disabled ? 600 : 400,
              color: disabled ? "var(--red)" : "var(--ink-2)",
              flexShrink: 0,
            }}
          >
            {statusLabel}
          </span>
        </div>

        {/* Tap-to-expand detail: Oddelenie + Pripojený (+ Účel návštevy) */}
        {expanded && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px solid var(--border-soft)",
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "6px 12px",
              alignItems: "baseline",
            }}
          >
            <span className="eyebrow" style={{ color: "var(--faint)" }}>
              {t("settings:department") as string}
            </span>
            <span style={{ fontSize: 12, color: "var(--ink-2)" }}>
              {user.department || "—"}
            </span>
            <span className="eyebrow" style={{ color: "var(--faint)" }}>
              {t("settings:joined") as string}
            </span>
            <span style={{ fontSize: 12, color: "var(--ink-2)", fontFamily: "var(--font-mono)" }}>
              {user.loginDate ? formatDate(user.loginDate) : (t("settings:notJoined") as string)}
            </span>
            {user.visitPurpose && (
              <>
                <span className="eyebrow" style={{ color: "var(--faint)" }}>
                  {t("settings:visitPurpose") as string}
                </span>
                <span style={{ fontSize: 12, color: "var(--ink-2)" }}>{user.visitPurpose}</span>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── Desktop / tablet grid row (§5.3) ───
  return (
    <div
      role="row"
      style={{
        display: "grid",
        gridTemplateColumns: TEAM_GRID,
        gap: 12,
        padding: "10px 16px",
        alignItems: "center",
        borderTop: "1px solid var(--border-soft)",
      }}
    >
      {/* Zamestnanec */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)", ...ellipsis }}>
          {user.firstName} {user.lastName}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--muted)", ...ellipsis }}>
          {user.userName}
        </div>
        {roleLabel && (
          <div style={{ fontSize: 11, color: "var(--faint)", ...ellipsis }}>{roleLabel}</div>
        )}
      </div>

      {/* Oddelenie */}
      <div style={{ fontSize: 12.5, color: "var(--ink-2)", minWidth: 0, ...ellipsis }}>
        {user.department ? user.department : <span style={{ color: "var(--faint)" }}>—</span>}
      </div>

      {/* Prístup */}
      <div style={{ minWidth: 0 }}>
        {user.accessType ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start", minWidth: 0 }}>
            <AccessPill accessType={user.accessType} t={t} />
            {user.visitPurpose && (
              <span style={{ fontSize: 11, color: "var(--muted)", maxWidth: "100%", ...ellipsis }}>
                {user.visitPurpose}
              </span>
            )}
          </div>
        ) : null}
      </div>

      {/* Pripojený */}
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)", minWidth: 0, ...ellipsis }}>
        {user.loginDate ? formatDate(user.loginDate) : <span style={{ color: "var(--faint)" }}>{t("settings:notJoined") as string}</span>}
      </div>

      {/* Platné do */}
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: expired ? 600 : 400,
            color: expired ? "var(--red)" : "var(--ink-2)",
            ...ellipsis,
          }}
        >
          {expirationText}
        </div>
        {remaining && !expired && remaining.text && (
          <div style={{ fontSize: 11, color: "var(--muted)" }}>{remaining.text}</div>
        )}
      </div>

      {/* Stav */}
      <div
        style={{
          fontSize: 12.5,
          fontWeight: disabled ? 600 : 400,
          color: disabled ? "var(--red)" : "var(--ink-2)",
          minWidth: 0,
          ...ellipsis,
        }}
      >
        {statusLabel}
      </div>

      {/* ⋮ */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>{menu}</div>
    </div>
  );
};

export default TeamRowV2;
